/**
 * Meta Ads na demonstração (0162).
 *
 * Nenhuma chamada sai para a Meta: a conta, as campanhas e os números por dia são
 * de demonstração e dizem ser. As regras são as do banco — período de até 400
 * dias, pausada sem gasto nem lead fica fora do painel, desconectar leva os
 * números e deixa os leads sem a campanha.
 */

import type { PortaDeDados } from './porta';
import type {
    AppDaMetaDaPlataforma, CampanhaDeAnuncio, CargaDoPainelDeAnuncios, ContaDaMeta, ContaDeAnuncioDaClinica, Lead,
    LinhaDoPainelDeAnuncios,
} from './tipos';

type MetodosDosAnuncios = Pick<PortaDeDados,
    'painelDeAnuncios' | 'contasDeAnuncio' | 'campanhasDeAnuncio' | 'conectarMeta' | 'contasDaMeta' | 'escolherContaDaMeta'
    | 'sincronizarMeta' | 'desconectarMeta' | 'appDaMeta' | 'salvarAppDaMeta' | 'removerAppDaMeta'>;

export interface DependenciasDosAnunciosNaDemo {
    leads: () => Lead[];
    pode: (permissao: string) => boolean;
    impersonando: () => boolean;
    operador: () => boolean;
    /** "Hoje" da demonstração, `YYYY-MM-DD`. */
    hoje: () => string;
}

interface CampanhaSemente {
    id: string;
    externoId: string;
    nome: string;
    status: string;
    objetivo: string;
    orcamentoDiarioCentavos: number;
    /** Gasto por dia, em centavos; zero é campanha parada. */
    gastoDiario: number;
}

/** Três campanhas: duas rodando e uma pausada sem gasto (que o painel esconde). */
export const CAMPANHAS_DE_ANUNCIO_SEMENTE: CampanhaSemente[] = [
    { id: 'ca-botox', externoId: '120210000000101', nome: 'Botox setembro — leads', status: 'ACTIVE', objetivo: 'OUTCOME_LEADS', orcamentoDiarioCentavos: 6000, gastoDiario: 5820 },
    { id: 'ca-harmonizacao', externoId: '120210000000102', nome: 'Harmonização — conversa no WhatsApp', status: 'ACTIVE', objetivo: 'OUTCOME_ENGAGEMENT', orcamentoDiarioCentavos: 4000, gastoDiario: 3710 },
    { id: 'ca-peeling', externoId: '120210000000103', nome: 'Peeling de inverno', status: 'PAUSED', objetivo: 'OUTCOME_TRAFFIC', orcamentoDiarioCentavos: 2500, gastoDiario: 0 },
];

/** As contas que o "login" da demonstração alcança. */
export const CONTAS_DA_META_SEMENTE: ContaDaMeta[] = [
    { id: 'act_100000000000001', nome: 'Clínica Vecchia · Anúncios', moeda: 'BRL', fuso: 'America/Sao_Paulo', ativa: true },
    { id: 'act_100000000000002', nome: 'Clínica Vecchia · Antiga', moeda: 'BRL', fuso: 'America/Sao_Paulo', ativa: false },
];

/** Leads da semente vindos de anúncio: um pelo WhatsApp, um escolhido pela equipe. */
const ATRIBUICOES_SEMENTE: { leadId: string; campanhaId: string; atribuicao: 'manual' | 'whatsapp-anuncio' }[] = [
    { leadId: 'l-marina', campanhaId: 'ca-harmonizacao', atribuicao: 'whatsapp-anuncio' },
    { leadId: 'l-taina', campanhaId: 'ca-botox', atribuicao: 'manual' },
];

const DIA = 86_400_000;

function diaMais(dia: string, n: number): string {
    return new Date(Date.parse(`${dia}T12:00:00Z`) + n * DIA).toISOString().slice(0, 10);
}

/** Número por dia que varia, mas sempre igual para o mesmo dia e campanha. */
function variacao(dia: string, semente: number): number {
    const n = Number(dia.replace(/-/g, '')) + semente;
    return ((n * 9301 + 49297) % 233280) / 233280;
}

let app: AppDaMetaDaPlataforma | null = null;
let conta: ContaDeAnuncioDaClinica | null = null;
let campanhas: CampanhaSemente[] = [];

function contaConectada(): ContaDeAnuncioDaClinica {
    return {
        id: 'cda-meta', plataforma: 'meta', contaExternaId: CONTAS_DA_META_SEMENTE[0]?.id ?? 'act_1',
        nome: CONTAS_DA_META_SEMENTE[0]?.nome ?? 'Conta', moeda: 'BRL', situacao: 'conectada',
        usuarioExterno: 'Ana Prado', conectadaPor: 'Ana Prado',
        conectadaEm: '2026-08-20T13:00:00-03:00', tokenExpiraEm: '2026-10-19T13:00:00-03:00',
        ultimaSincronizacaoEm: '2026-09-08T06:00:00-03:00',
    };
}

/** Volta ao começo. `leads` recebe as atribuições da semente. */
export function reiniciarAnunciosDaDemonstracao(leads: Lead[]): void {
    app = { appId: '123456789012345', dica: 'a1b2', atualizadoEm: '2026-09-01T10:00:00-03:00' };
    conta = contaConectada();
    campanhas = CAMPANHAS_DE_ANUNCIO_SEMENTE.map((c) => ({ ...c }));
    for (const a of ATRIBUICOES_SEMENTE) {
        const lead = leads.find((l) => l.id === a.leadId);
        const campanha = campanhas.find((c) => c.id === a.campanhaId);
        if (lead && campanha) {
            Object.assign(lead, { campanhaDeAnuncioId: campanha.id, campanhaDeAnuncio: campanha.nome, atribuicao: a.atribuicao });
        }
    }
}

/** O que `atualizarLead` grava na demonstração, com as regras dos gatilhos da 0162. */
export function aplicarCampanhaNoLead(lead: Lead, campanhaId: string): void {
    if (!campanhaId) {
        delete lead.campanhaDeAnuncioId;
        delete lead.campanhaDeAnuncio;
        delete lead.atribuicao;
        return;
    }
    const campanha = campanhas.find((c) => c.id === campanhaId);
    if (!campanha) throw new Error('Campanha de anúncio não encontrada nesta clínica.');
    if (lead.campanhaDeAnuncioId !== campanhaId) lead.atribuicao = 'manual';
    lead.campanhaDeAnuncioId = campanha.id;
    lead.campanhaDeAnuncio = campanha.nome;
}

function exigirGestao(dep: DependenciasDosAnunciosNaDemo): void {
    if (!dep.pode('marketing') || dep.impersonando()) {
        throw new Error('Esta ação exige a permissão de marketing nesta clínica.');
    }
}

function linhaDoPeriodo(c: CampanhaSemente, de: string, ate: string, leads: Lead[]): LinhaDoPainelDeAnuncios {
    let investido = 0;
    let impressoes = 0;
    let cliques = 0;
    let leadsPlataforma = 0;
    let conversas = 0;
    if (c.gastoDiario > 0) {
        for (let dia = de; dia <= ate; dia = diaMais(dia, 1)) {
            const v = variacao(dia, c.externoId.length + Number(c.externoId.slice(-1)));
            const gasto = Math.round(c.gastoDiario * (0.85 + v * 0.3));
            investido += gasto;
            const imp = Math.round(gasto * (0.9 + v * 0.4));
            impressoes += imp;
            cliques += Math.round(imp * 0.018);
            if (c.objetivo === 'OUTCOME_LEADS') leadsPlataforma += v > 0.55 ? 2 : 1;
            else conversas += v > 0.4 ? 2 : 1;
        }
    }
    const doCrm = leads.filter((l) => l.campanhaDeAnuncioId === c.id && l.criadoEm >= de && l.criadoEm <= ate);
    const ganhos = doCrm.filter((l) => l.situacao === 'ganho');
    return {
        id: c.id, externoId: c.externoId, nome: c.nome, status: c.status, objetivo: c.objetivo,
        orcamentoDiarioCentavos: c.orcamentoDiarioCentavos,
        investidoCentavos: investido, impressoes, cliques, leadsPlataforma, conversasIniciadas: conversas,
        leadsCrm: doCrm.length, ganhos: ganhos.length,
        receitaCentavos: ganhos.reduce((s, l) => s + l.valorEstimadoCentavos, 0),
    };
}

export function anunciosNaDemonstracao(dep: DependenciasDosAnunciosNaDemo): MetodosDosAnuncios {
    return {
        async contasDeAnuncio(): Promise<ContaDeAnuncioDaClinica[]> {
            if (!dep.pode('marketing')) throw new Error('Sem acesso ao Marketing desta clínica.');
            return conta ? [{ ...conta }] : [];
        },

        async painelDeAnuncios(dias: number): Promise<CargaDoPainelDeAnuncios> {
            if (!dep.pode('marketing')) throw new Error('Sem acesso ao Marketing desta clínica.');
            if (!Number.isInteger(dias) || dias < 1 || dias > 400) throw new Error('Período inválido (até 400 dias).');
            const ate = dep.hoje();
            const de = diaMais(ate, -(dias - 1));
            const leads = dep.leads();
            const linhas = conta?.situacao === 'conectada' || conta?.situacao === 'erro' || conta?.situacao === 'revogada'
                ? campanhas
                    .map((c) => linhaDoPeriodo(c, de, ate, leads))
                    .filter((l) => l.investidoCentavos > 0 || l.impressoes > 0 || l.leadsCrm > 0 || l.status === 'ACTIVE')
                    .sort((a, b) => b.investidoCentavos - a.investidoCentavos || a.nome.localeCompare(b.nome))
                : [];
            return {
                disponivel: app !== null, conta: conta ? { ...conta } : null, de, ate, dias, linhas,
                podeGerir: dep.pode('marketing') && !dep.impersonando(),
            };
        },

        async campanhasDeAnuncio(): Promise<CampanhaDeAnuncio[]> {
            if (!dep.pode('marketing')) return [];
            return campanhas.map((c) => ({ id: c.id, nome: c.nome, status: c.status })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
        },

        async conectarMeta(): Promise<string> {
            exigirGestao(dep);
            if (!app) throw new Error('A integração com a Meta ainda não foi configurada pela Fórmula MED.');
            /* Sem Meta de verdade: o "login" volta direto para a escolha da conta. */
            conta = { id: 'cda-meta', plataforma: 'meta', situacao: 'escolhendo', usuarioExterno: 'Ana Prado', conectadaPor: 'Ana Prado', conectadaEm: new Date().toISOString() };
            return '/app/configuracoes/integracoes?meta-ads=escolher';
        },

        async contasDaMeta(): Promise<ContaDaMeta[]> {
            exigirGestao(dep);
            if (!conta) throw new Error('Conecte a conta da Meta primeiro.');
            return CONTAS_DA_META_SEMENTE.map((c) => ({ ...c }));
        },

        async escolherContaDaMeta(contaId: string): Promise<void> {
            exigirGestao(dep);
            if (!conta) throw new Error('Conecte a conta da Meta primeiro.');
            const escolhida = CONTAS_DA_META_SEMENTE.find((c) => c.id === contaId);
            if (!escolhida) throw new Error('Esta conta de anúncios não está entre as que o login da Meta alcança.');
            if (conta.contaExternaId && conta.contaExternaId !== contaId) campanhas = [];
            else if (!campanhas.length) campanhas = CAMPANHAS_DE_ANUNCIO_SEMENTE.map((c) => ({ ...c }));
            conta = { ...conta, contaExternaId: escolhida.id, nome: escolhida.nome, moeda: escolhida.moeda, situacao: 'conectada', ultimaSincronizacaoEm: new Date().toISOString() };
        },

        async sincronizarMeta(): Promise<{ campanhas: number; linhas: number }> {
            exigirGestao(dep);
            if (!conta?.contaExternaId) throw new Error('Escolha a conta de anúncios primeiro.');
            conta = { ...conta, situacao: 'conectada', ultimaSincronizacaoEm: new Date().toISOString() };
            delete conta.ultimoErro;
            return { campanhas: campanhas.length, linhas: campanhas.filter((c) => c.gastoDiario > 0).length * 30 };
        },

        async desconectarMeta(): Promise<void> {
            exigirGestao(dep);
            conta = null;
            const ids = new Set(campanhas.map((c) => c.id));
            campanhas = [];
            for (const lead of dep.leads()) {
                if (lead.campanhaDeAnuncioId && ids.has(lead.campanhaDeAnuncioId)) {
                    delete lead.campanhaDeAnuncioId;
                    delete lead.campanhaDeAnuncio;
                    delete lead.atribuicao;
                }
            }
        },

        async appDaMeta(): Promise<AppDaMetaDaPlataforma | null> {
            if (!dep.operador()) throw new Error('Só quem opera a plataforma vê o app da Meta.');
            return app ? { ...app } : null;
        },

        async salvarAppDaMeta(appId: string, segredo: string, configId?: string): Promise<AppDaMetaDaPlataforma | null> {
            if (!dep.operador()) throw new Error('Só quem opera a plataforma configura a Meta.');
            if (!/^[0-9]{5,20}$/.test(appId.trim())) throw new Error('O ID do app da Meta é só números.');
            if (configId?.trim() && !/^[0-9]{5,20}$/.test(configId.trim())) throw new Error('O ID da configuração do Login para Empresas é só números.');
            const chave = segredo.trim();
            if (!chave && !app) throw new Error('Informe a chave secreta do app.');
            if (chave && !/^[0-9a-f]{32}$/.test(chave)) throw new Error('A chave secreta do app da Meta tem 32 caracteres (0-9, a-f).');
            app = {
                appId: appId.trim(),
                atualizadoEm: new Date().toISOString(),
                ...(chave ? { dica: chave.slice(-4) } : app?.dica ? { dica: app.dica } : {}),
                ...(configId?.trim() ? { configId: configId.trim() } : {}),
            };
            return { ...app };
        },

        async removerAppDaMeta(): Promise<void> {
            if (!dep.operador()) throw new Error('Só quem opera a plataforma configura a Meta.');
            app = null;
        },
    };
}
